import { useState } from 'react';
import { Building2, DollarSign, Users, AlertTriangle, TrendingUp } from 'lucide-react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { BuildingCard } from './buildings/BuildingCard';
import { BuildingsList } from './buildings/BuildingsList';
import { TabContainer, Tab } from '../ui';
import type { OwnedBuilding, BuildingCapacity, BuildingCosts } from '@/types/buildings';
import type { BuildingType } from '@/types/buildings';

interface BuildingsViewProps {
  buildings: OwnedBuilding[];
  availableBuildings: BuildingType[];
  capacity: BuildingCapacity;
  maintenanceCosts: BuildingCosts;
  capacityWarning: 'none' | 'low' | 'critical';
  cash: number;
  gameDate: { day: number; month: number; year: number };
  onPurchaseBuilding: (buildingTypeId: string) => boolean;
  onSellBuilding: (buildingId: string) => boolean;
  onUpgradeBuilding: (buildingId: string) => boolean;
}

export function BuildingsView({
  buildings,
  availableBuildings,
  capacity,
  maintenanceCosts,
  capacityWarning,
  cash,
  gameDate,
  onPurchaseBuilding,
  onSellBuilding,
  onUpgradeBuilding
}: BuildingsViewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'purchase' | 'owned'>('overview');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'office' | 'factory' | 'lab'>('all');

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'purchase', label: `Purchase Buildings (${availableBuildings.length})` },
    { id: 'owned', label: `Owned Buildings (${buildings.length})` },
  ];

  const filteredAvailableBuildings = (selectedCategory === 'all'
    ? availableBuildings
    : availableBuildings.filter(b => b.category === selectedCategory))
    .sort((a, b) => {
      // Sort by: 1) Can afford, 2) Cost
      const canAffordA = cash >= a.cost;
      const canAffordB = cash >= b.cost;

      if (canAffordA && !canAffordB) return -1;
      if (!canAffordA && canAffordB) return 1;

      // Sort by cost
      return a.cost - b.cost;
    });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-theme-text-primary mb-2">Buildings Management</h1>
        <p className="text-theme-text-secondary">Expand your company's infrastructure to support more employees and unlock bonuses</p>
      </div>

      {/* Capacity Warning */}
      {capacityWarning !== 'none' && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 bg-theme-bg-secondary border-l-4 ${
          capacityWarning === 'critical'
            ? 'border-theme-error'
            : 'border-theme-warning'
        }`}>
          <AlertTriangle className={`w-5 h-5 ${
            capacityWarning === 'critical' ? 'text-theme-error' : 'text-theme-warning'
          }`} />
          <div className="flex-1">
            <p className="font-medium text-theme-text-primary">
              {capacityWarning === 'critical' ? 'Critical Capacity Warning' : 'Low Capacity Warning'}
            </p>
            <p className="text-sm text-theme-text-secondary">
              You are using {capacity.used} of {capacity.total} employee slots ({Math.round((capacity.used / capacity.total) * 100)}% capacity)
            </p>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-theme-bg-content rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-theme-text-muted" />
            <span className="text-sm text-theme-text-muted">Capacity</span>
          </div>
          <div className="text-2xl font-bold text-theme-text-primary">{capacity.used}/{capacity.total}</div>
          <div className="text-sm text-theme-text-secondary mt-1">{capacity.available} available</div>
        </div>

        <div className="bg-theme-bg-content rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <Building2 className="w-5 h-5 text-theme-text-muted" />
            <span className="text-sm text-theme-text-muted">Buildings</span>
          </div>
          <div className="text-2xl font-bold text-theme-text-primary">{buildings.length}</div>
          <div className="text-sm text-theme-text-secondary mt-1">{availableBuildings.length} available</div>
        </div>

        <div className="bg-theme-bg-content rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-theme-text-muted" />
            <span className="text-sm text-theme-text-muted">Maintenance</span>
          </div>
          <div className="text-2xl font-bold text-theme-text-primary">{formatCurrency(maintenanceCosts.totalMaintenance)}</div>
          <div className="text-sm text-theme-text-secondary mt-1">per month</div>
        </div>

        <div className="bg-theme-bg-content rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-theme-text-muted" />
            <span className="text-sm text-theme-text-muted">Construction</span>
          </div>
          <div className="text-2xl font-bold text-theme-text-primary">
            {buildings.filter(b => b.isUnderConstruction).length}
          </div>
          <div className="text-sm text-theme-text-secondary mt-1">in progress</div>
        </div>
      </div>

      {/* Tabs and Content */}
      <TabContainer
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}
      >
        <div className="space-y-6">
        {activeTab === 'overview' && (
          <>
            {/* Capacity by Category */}
            <div className="bg-theme-bg-content rounded-lg border p-6">
              <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Capacity by Category</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-theme-text-secondary mb-1">Office Space</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{capacity.byCategory.office.used}</span>
                    <span className="text-theme-text-muted">/ {capacity.byCategory.office.total}</span>
                  </div>
                  <div className="text-xs text-theme-text-muted mt-1">
                    {capacity.byCategory.office.total - capacity.byCategory.office.used} slots available
                  </div>
                  <div className="mt-2 h-2 bg-theme-bg-tertiary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-theme-info transition-all"
                      style={{
                        width: `${capacity.byCategory.office.total > 0
                          ? (capacity.byCategory.office.used / capacity.byCategory.office.total) * 100
                          : 0}%`
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="text-sm text-theme-text-secondary mb-1">Factory Space</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{capacity.byCategory.factory.used}</span>
                    <span className="text-theme-text-muted">/ {capacity.byCategory.factory.total}</span>
                  </div>
                  <div className="text-xs text-theme-text-muted mt-1">
                    {capacity.byCategory.factory.total - capacity.byCategory.factory.used} slots available
                  </div>
                  <div className="mt-2 h-2 bg-theme-bg-tertiary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-theme-success transition-all"
                      style={{
                        width: `${capacity.byCategory.factory.total > 0
                          ? (capacity.byCategory.factory.used / capacity.byCategory.factory.total) * 100
                          : 0}%`
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="text-sm text-theme-text-secondary mb-1">Lab Space</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{capacity.byCategory.lab.used}</span>
                    <span className="text-theme-text-muted">/ {capacity.byCategory.lab.total}</span>
                  </div>
                  <div className="text-xs text-theme-text-muted mt-1">
                    {capacity.byCategory.lab.total - capacity.byCategory.lab.used} slots available
                  </div>
                  <div className="mt-2 h-2 bg-theme-bg-tertiary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-theme-accent transition-all"
                      style={{
                        width: `${capacity.byCategory.lab.total > 0
                          ? (capacity.byCategory.lab.used / capacity.byCategory.lab.total) * 100
                          : 0}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

          </>
        )}

        {activeTab === 'purchase' && (
          <>
            {/* Category Filter */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-theme-info text-white'
                    : 'bg-theme-bg-tertiary text-theme-text-secondary hover:bg-theme-bg-tertiary'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedCategory('office')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === 'office'
                    ? 'bg-theme-info text-white'
                    : 'bg-theme-bg-tertiary text-theme-text-secondary hover:bg-theme-bg-tertiary'
                }`}
              >
                Office
              </button>
              <button
                onClick={() => setSelectedCategory('factory')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === 'factory'
                    ? 'bg-theme-info text-white'
                    : 'bg-theme-bg-tertiary text-theme-text-secondary hover:bg-theme-bg-tertiary'
                }`}
              >
                Factory
              </button>
              <button
                onClick={() => setSelectedCategory('lab')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === 'lab'
                    ? 'bg-theme-info text-white'
                    : 'bg-theme-bg-tertiary text-theme-text-secondary hover:bg-theme-bg-tertiary'
                }`}
              >
                Lab
              </button>
            </div>

            {/* Available Buildings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAvailableBuildings.length > 0 ? (
                filteredAvailableBuildings.map(building => (
                  <BuildingCard
                    key={building.id}
                    building={building}
                    cash={cash}
                    gameDate={gameDate}
                    onPurchase={onPurchaseBuilding}
                  />
                ))
              ) : (
                <div className="col-span-2 text-center py-12 text-theme-text-muted">
                  No buildings available to purchase in this category
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'owned' && (
          <BuildingsList
            buildings={buildings}
            maintenanceCosts={maintenanceCosts}
            onSellBuilding={onSellBuilding}
            onUpgradeBuilding={onUpgradeBuilding}
            gameDate={gameDate}
            cash={cash}
          />
        )}
        </div>
      </TabContainer>
    </div>
  );
}